import { Box, Link, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { LICENCE_EXPIRY_WARNING_DAYS } from "@xop/dashboard-shared";
import { Folder, ShieldAlert } from "lucide-react";
import { useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";

import { RELEASE_STATUS_COLOR, statusLabel } from "@crm-src/pages/releases/release-status";

import { CARD_PX, PulseCard, PulseCardMessage, PulseCardSkeleton, PulseRows, PulseStats, type SystemRef } from "./PulseCard.js";
import { useApplicationWatchData, useLicenceWatchData, useReleaseWatchData } from "./useWatch.js";
import {
  expiryLabel,
  LICENCE_SOON_DAYS,
  recentReleases,
  shortDate,
  summariseApplications,
  summariseLicences,
  waitingLabel,
  type ReleaseTile,
} from "./watch.js";

/**
 * The three cards of the Company pulse's "Products & partners" column.
 *
 * Each one is mounted by `NeedsAttention` ONLY for a holder of the grant that
 * opens its CRM page — `crm:licences`, `crm:reseller_applications`,
 * `crm:products_pricing` — all super_admin-only in the baseline role bundle.
 * The data they read carries no money, so there is no financial wall to
 * respect here; the reseller wall is respected by showing licences by
 * CUSTOMER, never by reseller.
 */

const LICENCES: SystemRef = { label: "Licences", href: "/crm/licences", systemKey: "crm" };
const RESELLERS: SystemRef = { label: "Resellers", href: "/crm/administration/resellers", systemKey: "sales" };
const RELEASES: SystemRef = { label: "Software Releases", href: "/crm/releases", systemKey: "docs" };

/* ── Licences expiring ────────────────────────────────────────────────── */

export function LicencesWatchCard({ canApprove }: {
  /** Approving a licence is a super_admin act and nothing else, so the
   * "awaiting your approval" callout renders only for them. */
  canApprove: boolean;
}): JSX.Element {
  const query = useLicenceWatchData();
  const watch = useMemo(() => (query.data ? summariseLicences(query.data) : null), [query.data]);
  const total = watch ? watch.expired + watch.soon + watch.later : 0;

  return (
    <PulseCard
      system={LICENCES}
      title="Licences expiring"
      count={total}
      testId="watch-licences"
      footerHref={total > 0 ? "/crm/licences" : undefined}
    >
      {query.isLoading ? (
        <PulseCardSkeleton />
      ) : query.isError || !watch ? (
        <PulseCardMessage tone="warning" testId="watch-error">
          Couldn&apos;t load licences — try again shortly.
        </PulseCardMessage>
      ) : (
        <>
          <PulseStats
            testId="watch-licences-stats"
            stats={[
              { label: "Expired", value: watch.expired, tone: "error" },
              { label: `Within ${LICENCE_SOON_DAYS} days`, value: watch.soon, tone: "warning" },
              { label: `Within ${LICENCE_EXPIRY_WARNING_DAYS} days`, value: watch.later, tone: "neutral" },
            ]}
          />
          {canApprove && watch.pendingApproval.length > 0 && (
            <ApprovalCallout pending={watch.pendingApproval} />
          )}
          {watch.rows.length === 0 ? (
            <PulseCardMessage tone="good">
              No licences expire in the next {LICENCE_EXPIRY_WARNING_DAYS} days.
            </PulseCardMessage>
          ) : (
            <PulseRows
              rows={watch.rows.map((r) => ({
                id: `licence:${r.id}`,
                label: r.label ? `${r.customerName} · ${r.label}` : r.customerName,
                meta: expiryLabel(r.daysLeft),
                tone: r.tone,
                link: `/crm/licences/${r.id}`,
                badge: r.internal ? "Internal" : undefined,
              }))}
            />
          )}
        </>
      )}
    </PulseCard>
  );
}

function ApprovalCallout({ pending }: { pending: ReadonlyArray<{ id: number; customerName: string; label: string }> }): JSX.Element {
  const theme = useTheme();
  const first = pending[0];
  const one = pending.length === 1 && first;
  return (
    <Link
      component={RouterLink}
      to={one ? `/crm/licences/${first.id}` : "/crm/licences"}
      underline="none"
      data-testid="watch-licences-approvals"
      sx={{
        mx: CARD_PX,
        mb: 1.5,
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1.5,
        py: 1,
        borderRadius: 1,
        border: 1,
        borderColor: alpha(theme.palette.warning.main, 0.35),
        bgcolor: alpha(theme.palette.warning.main, 0.08),
        color: "text.primary",
        transition: "background-color 150ms",
        "&:hover": { bgcolor: alpha(theme.palette.warning.main, 0.14) },
      }}
    >
      <Box component="span" aria-hidden sx={{ display: "inline-flex", color: "warning.main", flexShrink: 0 }}>
        <ShieldAlert size={16} />
      </Box>
      <Typography sx={{ fontSize: 12, minWidth: 0, flex: 1 }}>
        <Box component="span" sx={{ fontWeight: 600 }}>
          {pending.length} licence{pending.length === 1 ? "" : "s"} awaiting your approval
        </Box>
        {one && (
          <Box component="span" sx={{ color: "text.secondary" }}>
            {" "}— {first.customerName}
          </Box>
        )}
      </Typography>
      <Box component="span" sx={{ fontSize: 11, fontWeight: 500, color: "text.secondary", flexShrink: 0 }}>
        Review →
      </Box>
    </Link>
  );
}

/* ── Reseller applications ────────────────────────────────────────────── */

export function ResellerApplicationsWatchCard(): JSX.Element {
  const query = useApplicationWatchData();
  const watch = useMemo(() => (query.data ? summariseApplications(query.data) : null), [query.data]);
  const total = watch ? watch.ready + watch.awaitingDocuments : 0;

  return (
    <PulseCard
      system={RESELLERS}
      title="Reseller applications"
      count={total}
      testId="watch-applications"
      footerHref={total > 0 ? "/crm/administration/resellers?tab=applications" : undefined}
    >
      {query.isLoading ? (
        <PulseCardSkeleton rows={2} />
      ) : query.isError || !watch ? (
        <PulseCardMessage tone="warning" testId="watch-error">
          Couldn&apos;t load applications — try again shortly.
        </PulseCardMessage>
      ) : (
        <>
          <PulseStats
            testId="watch-applications-stats"
            stats={[
              { label: "Ready to decide", value: watch.ready, tone: "warning" },
              { label: "Awaiting documents", value: watch.awaitingDocuments, tone: "neutral" },
            ]}
          />
          {watch.rows.length === 0 ? (
            <PulseCardMessage tone="good">No applications waiting on a decision.</PulseCardMessage>
          ) : (
            <PulseRows
              rows={watch.rows.map((r) => ({
                id: `application:${r.id}`,
                label: r.companyName,
                meta: `${r.statusLabel} · ${waitingLabel(r.waitingDays)}`,
                tone: r.ready ? "warn" : "neutral",
                link: `/crm/administration/reseller-applications/${r.id}`,
              }))}
            />
          )}
        </>
      )}
    </PulseCard>
  );
}

/* ── Latest releases ──────────────────────────────────────────────────── */

/** The newest releases as quick links: one tile per release folder, each
 * opening that release's own page (`/crm/releases/11`). */
export function ReleasesWatchCard(): JSX.Element {
  const query = useReleaseWatchData();
  const tiles = useMemo(() => (query.data ? recentReleases(query.data) : null), [query.data]);

  return (
    <PulseCard system={RELEASES} title="Latest releases" testId="watch-releases" footerHref="/crm/releases" footerLabel="All releases →">
      {query.isLoading ? (
        <PulseCardSkeleton rows={2} />
      ) : query.isError || !tiles ? (
        <PulseCardMessage tone="warning" testId="watch-error">
          Couldn&apos;t load releases — try again shortly.
        </PulseCardMessage>
      ) : tiles.length === 0 ? (
        <PulseCardMessage>No releases yet.</PulseCardMessage>
      ) : (
        <Box
          data-testid="watch-release-tiles"
          sx={{
            px: CARD_PX,
            pt: 0.5,
            pb: 2,
            display: "grid",
            gap: 1,
            // Wraps rather than shrinks: three tiles in a 250px card would be
            // three 70px slivers (2026-09-16). `auto-fit` keeps them side by
            // side while they fit and drops to two rows when they do not.
            gridTemplateColumns: `repeat(auto-fit, minmax(96px, 1fr))`,
          }}
        >
          {tiles.map((tile) => (
            <ReleaseTileLink key={tile.id} tile={tile} />
          ))}
        </Box>
      )}
    </PulseCard>
  );
}

function ReleaseTileLink({ tile }: { tile: ReleaseTile }): JSX.Element {
  const theme = useTheme();
  const accent = theme.palette.system.docs;
  const statusTone = RELEASE_STATUS_COLOR[tile.status] ?? "default";
  const statusColor = statusTone === "default" ? theme.palette.text.disabled : theme.palette[statusTone].main;
  const shipped = shortDate(tile.newestReleasedOn);
  const versions = `${tile.versionCount} version${tile.versionCount === 1 ? "" : "s"}`;

  return (
    <Link
      component={RouterLink}
      to={tile.href}
      underline="none"
      data-testid={`watch-release-${tile.release}`}
      aria-label={`Open release ${tile.release}`}
      title={tile.newestVersion ? `Latest version ${tile.newestVersion}${shipped ? `, shipped ${shipped}` : ""}` : undefined}
      sx={{
        minWidth: 0,
        display: "grid",
        gap: 0.25,
        px: 1.25,
        py: 1.25,
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        color: "text.primary",
        transition: "border-color 150ms ease, box-shadow 150ms ease, transform 150ms ease",
        "&:hover": { borderColor: "primary.main", boxShadow: 1, transform: "translateY(-1px)" },
        "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
      }}
    >
      <Box
        component="span"
        aria-hidden
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 26,
          height: 26,
          mb: 0.5,
          borderRadius: 1,
          bgcolor: alpha(accent, 0.35),
          color: "text.primary",
        }}
      >
        <Folder size={14} />
      </Box>
      <Box sx={{ fontSize: 10, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.08em", color: "text.disabled" }}>
        Release
      </Box>
      <Box
        sx={{
          fontFamily: theme.typography.h1.fontFamily,
          fontSize: 24,
          fontWeight: 600,
          lineHeight: 1.15,
          letterSpacing: "-0.01em",
          fontVariantNumeric: "tabular-nums",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {tile.release}
      </Box>
      {/* One short fact per line — a tile is a third of a card wide, so
          nothing here may need more than one line's width. */}
      <Box sx={{ fontSize: 12, fontWeight: 500, color: "text.secondary", fontVariantNumeric: "tabular-nums", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {tile.newestVersion ?? "No versions yet"}
      </Box>
      <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 0.5, minWidth: 0, fontSize: 10.5, fontWeight: 500, color: "text.secondary" }}>
        <Box component="span" aria-hidden sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: statusColor, flexShrink: 0 }} />
        <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {statusLabel(tile.status)}
        </Box>
      </Box>
      <Box sx={{ fontSize: 10.5, color: "text.disabled", whiteSpace: "nowrap" }}>{versions}</Box>
    </Link>
  );
}
